import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import MenuIcon from '@mui/icons-material/Menu'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MenuItem, Typography } from '@mui/material'
import CookiesStorageService from '../../services/CookiesStorageService'
import SingleNavLinks from './SingleNavLinks.jsx'
import UseTranslation from '../../hooks/UseTranslation'
import SwitchModulesBtn from './UserBtnSection/userMenu/SwitchModulesBtn.jsx'
import IconWithName from './UserBtnSection/userMenu/iconWithName.jsx'
import TranslationButton from '../Buttons/TranslationButton'
import { ReactComponent as HomeIcon } from '../../images/HomeNavIcon.svg'
import { ReactComponent as PasswordIcon } from '../../images/PasswordIcon.svg'
import { userAuthLogout, userAuthResetAlertHandler } from '../../redux/features/userAuth/userAuthThunk'
import { getFirstAndSecondCharacterOnly } from '../../utils/helpers'
import ChangePasswordModal from '../Modals/ChangePasswordModal.jsx'
import { ReactComponent as LogoutIcon } from '../../images/LogoutIcon.svg'
import LoaderWithErrorHandler from '../PageHelpers/LoaderWithErrorHandler.jsx'
import { selectAuthUser } from '../../redux/features/userAuth/userAuthSlice'
import { SettingsGearIcon, ChevronIcon } from '../../pages/Settings/icons.jsx'
import { isNativePlatform } from '../../services/Firebase/native'
import AppRoutes from '../../services/AppRoutes'

export const REOPEN_MORE_OPTIONS_EVENT = 'orgarise:open-more-options'

const NavDrawer = ({ MenuLinks }) => {
  // Settings renders its own AppBar/NavDrawer instance (see AuthRoute.js), so
  // going back from it unmounts this drawer and mounts a brand new one on the
  // page behind it - a plain window event fired at click time is gone before
  // that new instance's listener exists. A sessionStorage flag survives the
  // remount instead, so it can be read once when the next drawer mounts.
  const [drawerState, setDrawerState] = useState(() => {
    try {
      if (sessionStorage.getItem(REOPEN_MORE_OPTIONS_EVENT)) {
        sessionStorage.removeItem(REOPEN_MORE_OPTIONS_EVENT)
        return true
      }
    } catch (e) {
      return false
    }
    return false
  })
  const activeUrl = useLocation()
  const { t, language } = UseTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const [changePasswordModalState, setChangePasswordModalState] = useState(false)
  const userAuth = useSelector(selectAuthUser)
  const isArabic = language === 'AR'

  const toggleDrawer = () => {
    setDrawerState(!drawerState)
  }

  useEffect(() => {
    const reopen = () => setDrawerState(true)
    window.addEventListener(REOPEN_MORE_OPTIONS_EVENT, reopen)
    return () => window.removeEventListener(REOPEN_MORE_OPTIONS_EVENT, reopen)
  }, [])
  const logoutHandler = async () => {
    const logoutDone = await dispatch(userAuthLogout())

    if (logoutDone) {
      history.push('/')
    }
  }

  return (
    <Box sx={styles.root}>
        <Button
        onClick={toggleDrawer}
        sx={styles.drawerBtn}
      >
        <MenuIcon />
      </Button>

       <Drawer anchor="right" open={drawerState} onClose={toggleDrawer}>
           <Box
          onKeyDown={toggleDrawer}
        >
          <List sx={styles.drawerStyle}>
            <Box sx={styles.menuItemBtn}>
              <Box sx={styles.userShortName}>
                {getFirstAndSecondCharacterOnly(CookiesStorageService.getUserName())}
              </Box>
              <Box sx={styles.userNameBtn}>
                {CookiesStorageService.getUserName() || t?.['user name']}
              </Box>
            </Box>
            <Box sx={styles.featureSection} />
            <Box sx={{ width: 360 }} onKeyDown={toggleDrawer}>
              <List sx={styles.linkList}>
                {MenuLinks.map((page) => (
                  <ListItem key={page.key} disablePadding>
                    <SingleNavLinks
                      name={page.label}
                      to={page.link}
                      changeMenuStatusHandler={toggleDrawer}
                      active={activeUrl.pathname === page.link}
                      fullWidth
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={styles.optionSection}>
              <Typography>
                {t?.['More Options']}
              </Typography>
            </Box>
            <Box sx={styles.menuList}>
              {CookiesStorageService.getUserModules()?.length > 1 && (
              <MenuItem>
                <Box sx={styles.menuItem}>
                  <IconWithName
                    name={t?.['Welcome Screen']}
                   icon={<HomeIcon />}
                    onClick={() => {
                      history.push('/switch-modules')
                    }}
                  />
                </Box>
              </MenuItem>
              )}

              {CookiesStorageService.getUserModules()?.length > 1 && (
              <MenuItem>
                <Box sx={styles.menuItem}>
                  <SwitchModulesBtn />
                </Box>
              </MenuItem>
              )}
              {/* Device settings (push + biometrics) only exist inside the
                  native apps, so the entry is hidden in a mobile browser. */}
              {isNativePlatform() && (
              <MenuItem onClick={() => {
                toggleDrawer()
                history.push(AppRoutes.settings)
              }}>
                <Box sx={[styles.menuItem, styles.settingsItem]}>
                  <IconWithName name={t?.Settings} icon={<SettingsGearIcon size={17} />} />
                  <Box sx={styles.settingsArrow(isArabic)}>
                    <ChevronIcon size={16} />
                  </Box>
                </Box>
              </MenuItem>
              )}

              <MenuItem onClick={toggleDrawer}>
                <Box sx={styles.menuItem}>
                  <TranslationButton />
                </Box>
              </MenuItem>

              <MenuItem onClick={() => {
                setChangePasswordModalState(true)
                toggleDrawer()
              }}>
            <Box sx={styles.menuItem} >
              <IconWithName name={t?.['Change Password']} icon={<PasswordIcon />} />
            </Box>
        </MenuItem>

              <MenuItem onClick={logoutHandler}>
                <Box sx={styles.menuItem}>
                  <IconWithName name={t?.logout} icon={<LogoutIcon />} />
                </Box>
              </MenuItem>
            </Box>
          </List>
        </Box>
      </Drawer>

      <ChangePasswordModal
        formModalState={changePasswordModalState}
        setFormModalState={setChangePasswordModalState}
      />
     <LoaderWithErrorHandler store={userAuth} resetAlertHandler={userAuthResetAlertHandler}/>

    </Box>
  )
}

const styles = {
  root: {
    display: 'flex',
  },
  drawerBtn: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    fontSize: '18px',
    color: 'white',
    flexDirection: 'column',
    padding: '0px 12px',
    '& p': {
      fontSize: '0.75rem',
      color: '#44444F',
      textTransform: 'none',
    },
  },
  drawerStyle: {
    backgroundColor: '#F7F9FB',
    minHeight: '94vh',
    padding: '0px 0px 6vh 0px',
    '& a': {
      color: '#44444F !important',
      fontSize: '14px',
      padding: '10px 13px !important',
    },
    '& svg': {
      width: '17px',
      height: '17px',
      marginRight: '10px',
      marginTop: '2px',
    },
    '& button': {
      fontSize: '17px',
      fontWeight: '900',
      justifyContent: 'flex-start',
      borderRadius: 0,
      margin: '5px 0',
    },
  },
  userShortName: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50px',
    height: '50px',
    backgroundColor: 'primary.main',
    color: 'white',
    borderRadius: '12px',
    fontSize: '18px',
    fontFamily: 'Avenir-bold !important',
    textTransform: 'uppercase',
    marginRight: '20px',
  },
  userNameBtn: {
    fontSize: '16px',
    fontFamily: 'Avenir',
    maxWidth: '200px',
  },
  menuItemBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '15px',
    textTransform: 'capitalize',
    transition: 'all 0.5s',
    backgroundColor: '#FFFFFF',
  },
  featureSection: {
    backgroundColor: 'transparent',
    padding: '10px',
  },
  optionSection: {
    '& p': {
      fontSize: '13px',
      fontWeight: 800,
      lineHeight: '20.31px',
      backgroundColor: 'transparent',
      padding: '15px',
    },
  },
  settingsItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingsArrow: (flip) => ({
    display: 'inline-flex',
    marginInlineEnd: '10px',
    transform: flip ? 'scaleX(-1)' : 'none',
    '& svg': { width: '16px', height: '16px', marginRight: 0 },
  }),
  menuItem: {
    '& p': {
      fontSize: '12px !important',
      padding: '10px 0px !important',
      fontWeight: 350,
    },
    '& img': {
      filter: 'brightness(0.1) contrast(1.2)',
      width: '17px',
      height: '17px',
      marginRight: '8px !important',
    },
    '& svg': {
      color: '#44444F !important',
      width: '17px',
      height: '17px',
    },
  },
  linkList: {
    backgroundColor: '#FFFFFF',
  },
  menuList: {
    backgroundColor: '#FFFFFF',
  },
}

export default NavDrawer
